import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import type { IconOffset } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { RatingTextColor } from '@freelancer/ui/rating';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { ReviewCountSize } from './review-count.types';

@Component({
  standalone: false,
  selector: 'fl-review-count',
  template: `
    <fl-icon
      class="mr-xxxsmall"
      [name]="'ui-chat'"
      [disabled]="counter === 0"
      [size]="size"
      [sizeTablet]="sizeTablet"
      [sizeDesktop]="sizeDesktop"
      [sizeDesktopLarge]="sizeDesktopLarge"
      [offsetY]="iconOffsetY"
      [color]="IconColor.ORANGE_XDARK"
      [useIconFont]="true"
      [fillIconFont]="true"
    ></fl-icon>
    <span [class]="cls">
      {{ counter }}
    </span>
  `,
  styleUrls: ['./review-count.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewCountComponent implements OnChanges, OnInit {
  IconColor = IconColor;

  reviewTextSize: TextSize = TextSize.BODY_16;
  reviewTextSizeTablet?: TextSize | undefined;
  reviewTextSizeDesktop?: TextSize | undefined;
  reviewTextSizeDesktopLarge?: TextSize | undefined;

  @Input() counter = 0;
  @Input() textColor: RatingTextColor = 'foreground';

  @HostBinding('attr.data-size')
  @Input()
  size: ReviewCountSize = IconSize.MID;

  /** Change the [size] from tablet and above */
  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: ReviewCountSize;

  /** Change the [size] from desktop and above */
  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: ReviewCountSize;

  @HostBinding('attr.data-size-desktop-lage')
  @Input()
  sizeDesktopLarge?: ReviewCountSize;

  cls: string;

  /** Optical nudge for the mid-size chat glyph only. */
  get iconOffsetY(): IconOffset | undefined {
    return this.size === IconSize.MID ? 2 : undefined;
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    switch (this.size) {
      case IconSize.SMALL:
        this.reviewTextSize = TextSize.BODY_16;
        break;
      default:
        this.reviewTextSize = TextSize.BODY_16;
    }

    switch (this.sizeTablet) {
      case IconSize.SMALL:
        this.reviewTextSizeTablet = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.reviewTextSizeTablet = TextSize.BODY_16;
        break;
      default:
        this.reviewTextSizeTablet = undefined;
    }

    switch (this.sizeDesktop) {
      case IconSize.SMALL:
        this.reviewTextSizeDesktop = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.reviewTextSizeDesktop = TextSize.BODY_16;
        break;
      default:
        this.reviewTextSizeDesktop = undefined;
    }

    switch (this.sizeDesktopLarge) {
      case IconSize.SMALL:
        this.reviewTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.reviewTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      default:
        this.reviewTextSizeDesktopLarge = undefined;
    }

    if (
      'textColor' in changes ||
      'size' in changes ||
      'sizeTablet' in changes ||
      'sizeDesktop' in changes ||
      'sizeDesktopLarge' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.cls = textVariants({
      color:
        this.textColor === 'foreground'
          ? FontColor.FOREGROUND
          : FontColor.LIGHT,
      size: this.reviewTextSize,
      sizeTablet: this.reviewTextSizeTablet,
      sizeDesktop: this.reviewTextSizeDesktop,
      sizeDesktopLarge: this.reviewTextSizeDesktopLarge,
    });
  }
}
